import React, {useEffect, useMemo, useRef, useState} from 'react'
import PageLayout from '../../../components/layout/PageLayout'
import Text from '../../../components/text/Text'
import DateInput from '../../../components/input/DateInput'
import UnderLineInput from '../../../components/input/UnderLineInput'
import WizButton from '../../../components/button/WizButton'
import {useTime} from '../../../utils/time'
import {ICommonState, ICreateCampaignState} from './CampaignCreatePage'
import WizSelectButton from '../../../components/button/WizSelectButton'
import WizImage from '../../../components/image/WizImage'
import img_lot_main from '../../../assets/images/img_lot_main.jpg'
import {postUploadImage} from '../../../apis/image/postUploadImage'
import useQueryCampaign from '../../../hooks/api/campaign/useQueryCampaign'
import {useSearchParams} from 'react-router-dom'
import {editCampaign} from '../../../apis/campaign/editCampaign'
import useNavigation from '../../../hooks/useNavigation'
import WizSelect from '../../../components/select/WizSelect'

interface IProps {
    className?: string
}

interface IEditCampaignState extends ICommonState {
    id: string
    language: string
}

const CampaignDetailPage: React.FC<IProps> = ({className}) => {
    const [searchParams] = useSearchParams(document.location.search)
    const campaignId = searchParams.get('id')

    const {data} = useQueryCampaign(campaignId)

    const [campaign, setCampaign] = useState<ICreateCampaignState>()
    const [commonState, setCommonState] = useState<IEditCampaignState>()

    const {convertInputToTimezone, convertTimezoneToInput} = useTime()

    const {goCampaign} = useNavigation()

    const actionList = useMemo(
        () => [
            {id: 'web', value: 'Web'},
            {id: 'post', value: 'Post'},
            {id: 'league', value: 'League'},
            {id: 'deeplink', value: 'DeepLink'},
        ],
        [],
    )

    const languageList = useMemo(
        () => [
            {id: 'en', value: 'English'},
            {id: 'ko', value: 'Korean'},
        ],
        [],
    )

    const onChangeThumbnail = async e => {
        const imageFile = e?.target?.files[0]

        if (imageFile) {
            const result = await postUploadImage(imageFile)

            setCampaign({...campaign, background_image: result?.image_url})
        }
    }
    const bgImageRef = useRef<any>()

    const renderActionPlaceholder = () => {
        if (campaign?.action_type === 'web') {
            return 'Action (Web Link)'
        } else if (campaign?.action_type === 'post') {
            return 'Action (Post Id)'
        } else if (campaign?.action_type === 'league') {
            return 'Action (League Id)'
        } else {
            return 'Action (Deep Link)'
        }
    }

    const onClickEditCampaign = async () => {
        const resBody = {
            ...campaign,
            id: commonState.id,
            language: commonState.language,
            start_at: convertInputToTimezone(commonState.start_at),
            end_at: convertInputToTimezone(commonState.end_at),
            sorting: commonState.sorting,
        }

        const {data} = await editCampaign(resBody)

        if (data?.success) {
            alert(`Campaign Edit Complete!`)
            goCampaign()
        } else {
            alert(`Campaign Edit Error!`)
        }
    }

    useEffect(() => {
        setCampaign({
            name: data?.data?.name,
            action_type: data?.data?.action_type,
            action: data?.data?.action,
            background_color: {
                light: data?.data?.background_color.light,
                dark: data?.data?.background_color.dark,
            },
            background_image: data?.data?.background_image,
        })

        setCommonState({
            start_at: convertTimezoneToInput(data?.data?.start_at),
            end_at: convertTimezoneToInput(data?.data?.end_at),
            sorting: data?.data?.sorting,
            id: data?.data?.id,
            language: data?.data?.language,
        })
    }, [data])

    const onChangeFunction = e => {
        setCampaign({...campaign, action: '', action_type: e})
    }

    return (
        <PageLayout title={'Edit Bottom Sheet'}>
            <div className={'mt-[30px] max-w-[1000px] mx-auto flex gap-[10px]'}>
                <div
                    className={
                        'w-[480px] bg-white rounded-[10px] shadow-normal mx-auto py-[40px] px-[35px] space-y-[30px] md:w-full'
                    }>
                    <Text className={'text-h3 text-center sm:text-h1'}>
                        {/*{commonState.language === 'en' ? 'English Campaign' : 'Korean Campaign'}*/}
                        Campaign
                    </Text>
                    <div className={''}>
                        <Text className={'text-body3 text-font02'}>Name</Text>
                        <UnderLineInput
                            className={'w-full'}
                            value={campaign?.name}
                            onChanged={text => {
                                setCampaign({...campaign, name: text})
                            }}
                        />
                    </div>

                    <div className={'space-y-[3px] md:space-y-[7px]'}>
                        <Text className={'text-body3 text-font02 text-center'}>Link Type</Text>
                        <WizSelectButton
                            className={'mx-auto md:hidden'}
                            data={actionList}
                            onChange={e => {
                                setCampaign({...campaign, action: '', action_type: e.id})
                            }}
                            activeValue={actionList?.find(item => item.id === campaign?.action_type)}
                        />

                        <WizSelect
                            optionKey="campaign_action_type"
                            className={'hidden md:block md:mx-auto md:w-min'}
                            onChange={onChangeFunction}
                        />
                    </div>

                    <div className={''}>
                        <Text className={'text-body3 text-font02'}>Action</Text>
                        <UnderLineInput
                            placeholder={renderActionPlaceholder()}
                            value={campaign?.action}
                            onChanged={text => {
                                setCampaign({...campaign, action: text})
                            }}
                        />
                    </div>
                    <div className={'flex justify-between items-center gap-x-[30px]'}>
                        <div className={'flex-1'}>
                            <Text className={'text-body3 text-font02'}>Background Color (Dark Mode)</Text>
                            <div className={'flex items-center gap-x-[5px]'}>
                                <UnderLineInput
                                    placeholder={'#FFFFFF'}
                                    value={campaign?.background_color?.dark}
                                    onChanged={text => {
                                        setCampaign({
                                            ...campaign,
                                            background_color: {...campaign.background_color, dark: text},
                                        })
                                    }}
                                />
                                <div
                                    style={{backgroundColor: campaign?.background_color?.dark}}
                                    className={'w-[16px] h-[16px] rounded-[5px] border border-line01'}
                                />
                            </div>
                        </div>
                        <div className={'flex-1'}>
                            <Text className={'text-body3 text-font02'}>Background Color (Light Mode)</Text>
                            <div className={'flex items-center gap-x-[5px]'}>
                                <UnderLineInput
                                    placeholder={'#000000'}
                                    value={campaign?.background_color.light}
                                    onChanged={text => {
                                        setCampaign({
                                            ...campaign,
                                            background_color: {...campaign.background_color, light: text},
                                        })
                                    }}
                                />
                                <div
                                    style={{backgroundColor: campaign?.background_color?.light}}
                                    className={'w-[16px] h-[16px] rounded-[5px] border border-line01'}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={'flex justify-between items-end gap-x-[30px]'}>
                        <div className={'flex-1'}>
                            <Text className={'text-body3 text-font02'}>Background Image (Korean)</Text>
                            <div
                                onClick={() => bgImageRef?.current?.click()}
                                className={
                                    'border border-line02 rounded-[10px] overflow-hidden w-full mt-[10px] cursor-pointer flex relative'
                                }>
                                <div className={'relative brightness-50'}>
                                    <WizImage src={img_lot_main} />
                                </div>
                                {campaign?.background_image && (
                                    <div
                                        style={{backgroundColor: campaign?.background_color?.dark}}
                                        className={'w-full absolute bottom-0 left-0 z-[1] pt-[20px] rounded-[10px]'}>
                                        <WizImage src={`${campaign?.background_image}?type=webp&width=1920`} />
                                    </div>
                                )}
                            </div>
                            <input
                                type={'file'}
                                onChange={e => onChangeThumbnail(e)}
                                ref={bgImageRef}
                                className={'hidden'}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={'mt-[30px]'}>
                <div
                    className={
                        'w-[480px] bg-white rounded-[10px] shadow-normal mx-auto py-[40px] px-[85px] space-y-[40px] sm:w-full sm:px-[40px]'
                    }>
                    <WizSelectButton
                        className={'mx-auto'}
                        data={languageList}
                        onChange={e => {
                            setCommonState({...commonState, language: e.id})
                        }}
                        activeValue={languageList?.find(item => item.id === commonState?.language)}
                    />
                    <div className={'text-center'}>
                        <Text className={'text-body3 text-font02'}>Start At</Text>
                        <DateInput
                            value={commonState?.start_at as string}
                            onChange={e => {
                                setCommonState({...commonState, start_at: e.target.value})
                            }}
                        />
                    </div>
                    <div className={'text-center'}>
                        <Text className={'text-body3 text-font02'}>End At</Text>
                        <DateInput
                            value={commonState?.end_at as string}
                            onChange={e => {
                                setCommonState({...commonState, end_at: e.target.value})
                            }}
                        />
                    </div>
                    <div>
                        <Text className={'text-body3 text-font02 '}>Sorting (Order)</Text>
                        <UnderLineInput
                            value={commonState?.sorting}
                            onChanged={text => {
                                setCommonState({...commonState, sorting: Number(text)})
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className={'mt-[30px] text-center'}>
                <WizButton
                    type={'primary'}
                    size={'big'}
                    text={'Edit Campaign'}
                    onClick={() => {
                        onClickEditCampaign()
                    }}
                />
            </div>
        </PageLayout>
    )
}

export default CampaignDetailPage
